import { AsyncPipe } from '@angular/common';
import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  OnInit,
  inject,
  input,
  viewChild,
} from '@angular/core';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { ColDef } from 'ag-grid-community';
import { Observable } from 'rxjs';

import { BarcodeTypes } from '../../constants';
import { ScannerStockDetails } from '../../models';
import {
  AggFunc,
  BaseFormComponent,
  FormlyService,
  GridService,
  ReadonlyGridComponent,
  SummaryTopComponent,
} from '../../../tms-shared';
import { ModalInfoService } from '../../../tms-shared/services/component-services/modal-info.service';

@Component({
  selector: 'app-base-view-stock',
  templateUrl: './base-view-stock.component.html',
  styleUrl: './base-view-stock.component.scss',
  imports: [SummaryTopComponent, BaseFormComponent, ReadonlyGridComponent, AsyncPipe],
})
export class BaseViewStockComponent implements OnInit, AfterViewInit, AfterViewChecked {
  private readonly formlyService = inject(FormlyService);
  private readonly gridService = inject(GridService);
  private readonly modalInfoService = inject(ModalInfoService);
  private readonly changeDetector = inject(ChangeDetectorRef);

  private readonly form = viewChild<BaseFormComponent>('viewStockForm');
  public readonly header = input<string>();
  public readonly extraFields = input(false);
  public readonly getStock = input<(location: string) => Observable<ScannerStockDetails[]>>();
  public readonly customColumnDefs = input<ColDef[]>();
  public readonly gridCssClass = input('scanner-grid');
  public readonly showItemCount = input(false);

  protected model$: Observable<ScannerStockDetails[]>;
  protected fields: FormlyFieldConfig[];

  protected columnDefs: ColDef[] = [
    this.gridService.text('stockNo', $localize`Stock Number`, { aggFunc: AggFunc.count }),
    this.gridService.text('batchNo', $localize`Batch`),
    this.gridService.text('productKey', $localize`Product`),
    this.gridService.text('balance', $localize`Balance`, { aggFunc: AggFunc.sum }),
  ];

  ngOnInit(): void {
    if (this.customColumnDefs()) {
      this.columnDefs = this.customColumnDefs();
    } else if (this.extraFields()) {
      this.columnDefs.push(
        this.gridService.text('grade', $localize`Grade`),
        this.gridService.date('dateIntoStock', $localize`Date Into Stock`),
      );
    }
    this.getFields();
  }

  ngAfterViewInit(): void {
    this.focusField();
  }

  ngAfterViewChecked(): void {
    this.changeDetector.detectChanges();
  }

  private getFields() {
    this.fields = [
      this.formlyService.text('locationKey', $localize`Location`, {
        enter: () => this.getModel(),
        blur: () => this.getModel(),
        focus: () => this.clearLocation(),
        backspaceReset: true,
      }),
      this.formlyService.button($localize`Next Location`, () => this.clearLocation()),
    ];
  }

  private clearLocation() {
    this.form().updateModel({ locationKey: undefined });
    this.model$ = undefined;
    this.focusField();
  }

  private getModel() {
    // Trim any trailing carriage return / whitespace the hand scanner may append, otherwise the
    // stock lookup won't match the scanned label. (Matching is case-insensitive server-side.)
    const location = (this.form().model().locationKey ?? '').trim();
    const barcodeType = location.slice(0, 2);
    if (
      barcodeType !== BarcodeTypes.stockItem &&
      barcodeType !== BarcodeTypes.job &&
      barcodeType !== BarcodeTypes.machine &&
      barcodeType !== BarcodeTypes.piece &&
      barcodeType !== BarcodeTypes.operator &&
      barcodeType !== BarcodeTypes.warehouseAllocation
    ) {
      this.model$ = this.getStock()(location);
    } else {
      this.modalInfoService.infoSmall($localize`Location Stock`, $localize`Invalid Barcode Type`);
    }
  }

  private focusField() {
    this.formlyService.focusElement('locationKey');
  }
}
